import React, { useEffect, useRef } from 'react'
import { useAppTheme } from '../hooks/useAppTheme'

/**
 * Фирменный анимированный фон DeepRoller — «Liquid Silk».
 * Тягучие бирюзовые «фасции» перетекают в глубине (метафора роллера
 * глубокой проработки). 5 слоёв волн (сумма синусов) + блик по гребню.
 * Монтируется один раз глобально, лежит фиксированным слоем за контентом.
 * Уважает prefers-reduced-motion, чистит rAF/слушатели на размонтировании.
 */

const CSS = `
.dpr-silk-bg{position:fixed;inset:0;z-index:-1;overflow:hidden;pointer-events:none;background:#0b1f22;isolation:isolate;}
.dpr-silk-bg .l-base{position:absolute;inset:0;background:radial-gradient(130% 100% at 28% 0%,#0f2d31 0%,#0b2226 42%,#08181a 100%);}
.dpr-silk-bg .l-glow{position:absolute;border-radius:50%;filter:blur(60px);opacity:.55;will-change:transform;}
.dpr-silk-bg .l-glow-a{width:60%;height:70%;left:-10%;top:-20%;background:radial-gradient(circle at 50% 50%,rgba(42,111,94,0.5),rgba(42,111,94,0) 65%);animation:dpr_silk_glowA 52s ease-in-out infinite;}
.dpr-silk-bg .l-glow-b{width:70%;height:80%;right:-15%;bottom:-25%;background:radial-gradient(circle at 50% 50%,rgba(74,143,150,0.42),rgba(74,143,150,0) 65%);animation:dpr_silk_glowB 64s ease-in-out infinite;}
.dpr-silk-bg .l-glow-warm{width:34%;height:40%;right:6%;top:8%;opacity:.4;filter:blur(70px);background:radial-gradient(circle at 50% 50%,rgba(232,195,122,0.16),rgba(232,195,122,0) 60%);animation:dpr_silk_warm 46s ease-in-out infinite;}
.dpr-silk-bg .l-canvas{position:absolute;inset:0;width:100%;height:100%;display:block;}
.dpr-silk-bg .l-sheen{position:absolute;inset:-20%;background:linear-gradient(115deg,rgba(159,224,216,0) 38%,rgba(159,224,216,0.06) 50%,rgba(159,224,216,0) 62%);mix-blend-mode:screen;opacity:.6;animation:dpr_silk_sheen 34s ease-in-out infinite;will-change:transform;}
.dpr-silk-bg .l-grain{position:absolute;inset:0;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='dprn'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23dprn)'/%3E%3C/svg%3E");background-size:160px 160px;opacity:.05;mix-blend-mode:overlay;}
.dpr-silk-bg .l-vignette{position:absolute;inset:0;background:radial-gradient(125% 105% at 50% 42%,rgba(4,12,13,0) 52%,rgba(4,12,13,0.55) 100%);}
@keyframes dpr_silk_glowA{0%{transform:translate3d(-4%,-3%,0) scale(1);}50%{transform:translate3d(6%,5%,0) scale(1.16);}100%{transform:translate3d(-4%,-3%,0) scale(1);}}
@keyframes dpr_silk_glowB{0%{transform:translate3d(3%,4%,0) scale(1.05);}50%{transform:translate3d(-5%,-4%,0) scale(1.2);}100%{transform:translate3d(3%,4%,0) scale(1.05);}}
@keyframes dpr_silk_warm{0%{transform:translate3d(0,0,0) scale(1);opacity:.32;}50%{transform:translate3d(-4%,3%,0) scale(1.12);opacity:.46;}100%{transform:translate3d(0,0,0) scale(1);opacity:.32;}}
@keyframes dpr_silk_sheen{0%{transform:translate3d(-12%,0,0);}50%{transform:translate3d(12%,0,0);}100%{transform:translate3d(-12%,0,0);}}
@media (prefers-reduced-motion: reduce){.dpr-silk-bg .l-glow,.dpr-silk-bg .l-glow-warm,.dpr-silk-bg .l-sheen{animation:none;}}
`

const TAU = Math.PI * 2

// 5 слоёв «фасций»: base — базовая высота (доля H), a/w/s — амплитуды/частоты/скорости
// двух гармоник, p — фазовый сдвиг, drift/ds — медленное дыхание всей волны,
// stops — вертикальный градиент заливки, crest — цвет блика по гребню (или null).
const LAYERS = [
  { base: 0.42, a1: 0.030, a2: 0.018, w1: 1.0, w2: 2.2, s1: 0.045, s2: 0.030, p: 0.0, drift: 0.010, ds: 0.050,
    stops: [[0, 'rgba(24,64,66,0.26)'], [0.5, 'rgba(16,42,46,0.13)'], [1, 'rgba(11,31,34,0)']], crest: null },
  { base: 0.58, a1: 0.045, a2: 0.020, w1: 1.2, w2: 2.6, s1: 0.065, s2: 0.042, p: 1.7, drift: 0.012, ds: 0.060,
    stops: [[0, 'rgba(30,84,80,0.32)'], [0.14, 'rgba(60,120,116,0.19)'], [0.6, 'rgba(20,54,56,0.11)'], [1, 'rgba(11,31,34,0)']], crest: null },
  { base: 0.70, a1: 0.050, a2: 0.022, w1: 1.05, w2: 2.9, s1: 0.058, s2: 0.050, p: 3.1, drift: 0.014, ds: 0.045,
    stops: [[0, 'rgba(42,111,94,0.38)'], [0.12, 'rgba(92,152,144,0.22)'], [0.6, 'rgba(24,64,62,0.13)'], [1, 'rgba(9,26,28,0)']], crest: 'rgba(159,224,216,0.07)' },
  { base: 0.80, a1: 0.055, a2: 0.024, w1: 1.3, w2: 3.2, s1: 0.082, s2: 0.055, p: 4.6, drift: 0.013, ds: 0.070,
    stops: [[0, 'rgba(74,143,150,0.32)'], [0.1, 'rgba(120,182,180,0.2)'], [0.55, 'rgba(30,80,84,0.12)'], [1, 'rgba(9,26,28,0)']], crest: null },
  { base: 0.90, a1: 0.050, a2: 0.020, w1: 1.1, w2: 3.6, s1: 0.090, s2: 0.060, p: 5.9, drift: 0.011, ds: 0.050,
    stops: [[0, 'rgba(107,172,179,0.30)'], [0.08, 'rgba(159,224,216,0.18)'], [0.5, 'rgba(60,130,130,0.1)'], [1, 'rgba(9,26,28,0)']], crest: 'rgba(159,224,216,0.08)' },
]

export default function AnimatedBackground() {
  const { dark } = useAppTheme()
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !canvas.getContext) return undefined
    const ctx = canvas.getContext('2d')
    const mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null
    let reduce = mq ? mq.matches : false
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    let W = 0
    let H = 0
    let raf = null
    let startT = null
    let resizePending = false

    const waveY = (u, L, t) =>
      H * L.base + H * L.drift * Math.sin(t * L.ds + L.p)
      + H * L.a1 * Math.sin(u * TAU * L.w1 + t * L.s1 + L.p)
      + H * L.a2 * Math.sin(u * TAU * L.w2 - t * L.s2 + L.p * 0.6)

    const buildEdge = (L, t) => {
      const steps = Math.max(14, Math.floor(W / 58))
      const pts = []
      for (let i = 0; i <= steps; i += 1) {
        const u = i / steps
        pts.push([u * W, waveY(u, L, t)])
      }
      return pts
    }

    const traceTop = (pts) => {
      ctx.moveTo(pts[0][0], pts[0][1])
      for (let i = 1; i < pts.length; i += 1) {
        const mx = (pts[i - 1][0] + pts[i][0]) / 2
        const my = (pts[i - 1][1] + pts[i][1]) / 2
        ctx.quadraticCurveTo(pts[i - 1][0], pts[i - 1][1], mx, my)
      }
      const n = pts.length - 1
      ctx.lineTo(pts[n][0], pts[n][1])
    }

    const drawLayer = (L, t) => {
      const pts = buildEdge(L, t)
      ctx.beginPath()
      traceTop(pts)
      ctx.lineTo(W, H + 4)
      ctx.lineTo(0, H + 4)
      ctx.closePath()
      let top = H * L.base - H * (L.a1 + L.a2 + L.drift) - 10
      if (top < 0) top = 0
      const g = ctx.createLinearGradient(0, top, 0, H)
      for (let i = 0; i < L.stops.length; i += 1) g.addColorStop(L.stops[i][0], L.stops[i][1])
      ctx.fillStyle = g
      ctx.fill()
      if (L.crest) {
        ctx.beginPath()
        traceTop(pts)
        ctx.globalCompositeOperation = 'screen'
        ctx.strokeStyle = L.crest
        ctx.lineWidth = 1.4
        ctx.stroke()
        ctx.globalCompositeOperation = 'source-over'
      }
    }

    const draw = (t) => {
      ctx.clearRect(0, 0, W, H)
      for (let i = 0; i < LAYERS.length; i += 1) drawLayer(LAYERS[i], t)
    }

    const frame = (now) => {
      if (startT === null) startT = now
      draw((now - startT) / 1000)
      raf = requestAnimationFrame(frame)
    }

    const play = () => {
      if (reduce || raf !== null) return
      raf = requestAnimationFrame(frame)
    }

    const stop = () => {
      if (raf !== null) {
        cancelAnimationFrame(raf)
        raf = null
      }
    }

    const applySize = () => {
      const w = canvas.clientWidth || canvas.offsetWidth || 1
      const h = canvas.clientHeight || canvas.offsetHeight || 1
      W = Math.max(1, w)
      H = Math.max(1, h)
      canvas.width = Math.round(W * dpr)
      canvas.height = Math.round(H * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      if (reduce || raf === null) draw(reduce ? 9.0 : 0)
    }

    const onResize = () => {
      if (resizePending) return
      resizePending = true
      requestAnimationFrame(() => { resizePending = false; applySize() })
    }

    const onVisibility = () => {
      if (document.hidden) stop()
      else play()
    }

    const onMq = () => {
      reduce = mq.matches
      if (reduce) { stop(); applySize() } else { startT = null; play() }
    }

    applySize()
    window.addEventListener('resize', onResize)
    document.addEventListener('visibilitychange', onVisibility)
    let ro = null
    if (window.ResizeObserver) {
      ro = new ResizeObserver(onResize)
      ro.observe(canvas)
    }
    if (mq) {
      if (mq.addEventListener) mq.addEventListener('change', onMq)
      else if (mq.addListener) mq.addListener(onMq)
    }
    if (reduce) applySize()
    else play()

    return () => {
      stop()
      window.removeEventListener('resize', onResize)
      document.removeEventListener('visibilitychange', onVisibility)
      if (ro) ro.disconnect()
      if (mq) {
        if (mq.removeEventListener) mq.removeEventListener('change', onMq)
        else if (mq.removeListener) mq.removeListener(onMq)
      }
    }
  }, [dark])

  // Фон рассчитан на тёмную тему; в светлой остаётся штатная светлая подложка.
  if (!dark) return null

  return (
    <div className="dpr-silk-bg" aria-hidden="true">
      <style>{CSS}</style>
      <div className="l-base" />
      <div className="l-glow l-glow-a" />
      <div className="l-glow l-glow-b" />
      <div className="l-glow l-glow-warm" />
      <canvas ref={canvasRef} className="l-canvas" />
      <div className="l-sheen" />
      <div className="l-grain" />
      <div className="l-vignette" />
    </div>
  )
}
